/**
 * AI Workspace Large Database Filter Dialog
 *
 * Dialog shown when a database has more than 100 objects of any type.
 * Allows Pro users to choose what to export: Don't load, Load all, or Select specific items.
 * Includes a note about MCP integration for full schema access on-demand.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { DualListPicker, type DualListItem } from '@/components/ui/dual-list-picker';
import {
  Table2,
  Eye,
  FileCode,
  FunctionSquare,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Plug,
} from 'lucide-react';
import type { SchemaObjectNameItem } from '@/types/electron';
import type { SchemaExportCounts, SchemaExportFilter } from '@/types/terminal';

type ObjectType = 'table' | 'view' | 'procedure' | 'function';
type FilterOption = 'none' | 'all' | 'select';

type ObjectTypeConfig = {
  type: ObjectType;
  label: string;
  pluralLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  count: number;
};

type ObjectTypeState = {
  option: FilterOption;
  selectedIds: string[];
  isExpanded: boolean;
};

type AiWorkspaceLargeDbDialogProps = {
  isOpen: boolean;
  sessionId: string;
  connectionId: string;
  databaseName: string;
  counts: SchemaExportCounts;
  onConfirm: (filter: SchemaExportFilter) => void;
  onCancel: () => void;
};

const LARGE_THRESHOLD = 100;

const OBJECT_TYPE_CONFIGS: Omit<ObjectTypeConfig, 'count'>[] = [
  { type: 'table', label: 'Tables', pluralLabel: 'tables', icon: Table2 },
  { type: 'view', label: 'Views', pluralLabel: 'views', icon: Eye },
  { type: 'procedure', label: 'Stored Procedures', pluralLabel: 'procedures', icon: FileCode },
  { type: 'function', label: 'Functions', pluralLabel: 'functions', icon: FunctionSquare },
];

function getCountForType(counts: SchemaExportCounts, type: ObjectType): number {
  switch (type) {
    case 'table':
      return counts.tableCount;
    case 'view':
      return counts.viewCount;
    case 'procedure':
      return counts.procedureCount;
    case 'function':
      return counts.functionCount;
  }
}

export function AiWorkspaceLargeDbDialog({
  isOpen,
  sessionId,
  connectionId,
  databaseName,
  counts,
  onConfirm,
  onCancel,
}: AiWorkspaceLargeDbDialogProps) {
  // State for each object type
  const [typeStates, setTypeStates] = useState<Record<ObjectType, ObjectTypeState>>({
    table: { option: 'all', selectedIds: [], isExpanded: false },
    view: { option: 'all', selectedIds: [], isExpanded: false },
    procedure: { option: 'all', selectedIds: [], isExpanded: false },
    function: { option: 'all', selectedIds: [], isExpanded: false },
  });

  // Object names for dual-list pickers
  const [objectNames, setObjectNames] = useState<Record<ObjectType, DualListItem[]>>({
    table: [],
    view: [],
    procedure: [],
    function: [],
  });

  // Loading states for object names
  const [loadingNames, setLoadingNames] = useState<Record<ObjectType, boolean>>({
    table: false,
    view: false,
    procedure: false,
    function: false,
  });

  // Build object type configs with counts
  const typeConfigs: ObjectTypeConfig[] = OBJECT_TYPE_CONFIGS.map((config) => ({
    ...config,
    count: getCountForType(counts, config.type),
  }));

  // Only show types that have objects
  const activeTypes = typeConfigs.filter((config) => config.count > 0);

  // Types that exceed the threshold
  const largeTypes = typeConfigs.filter((config) => config.count > LARGE_THRESHOLD);

  // Fetch object names when expanding a type with 'select' option
  const fetchObjectNames = useCallback(
    async (type: ObjectType) => {
      if (objectNames[type].length > 0 || loadingNames[type]) return;

      setLoadingNames((prev) => ({ ...prev, [type]: true }));

      try {
        const result = await window.electronAPI?.getSchemaObjectNames({
          connectionId,
          database: databaseName,
          objectType: type,
        });

        if (result?.success && result.data) {
          const items: DualListItem[] = result.data.map((obj: SchemaObjectNameItem) => ({
            id: obj.id,
            name: obj.name,
            schema: obj.schema,
          }));
          setObjectNames((prev) => ({ ...prev, [type]: items }));
        }
      } catch (error) {
        console.error(`[AiWorkspaceLargeDbDialog] Failed to fetch ${type} names:`, error);
      } finally {
        setLoadingNames((prev) => ({ ...prev, [type]: false }));
      }
    },
    [connectionId, databaseName, objectNames, loadingNames]
  );

  // Handle option change
  const handleOptionChange = useCallback(
    (type: ObjectType, option: FilterOption) => {
      setTypeStates((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          option,
          isExpanded: option === 'select',
        },
      }));

      // Fetch names if selecting specific items
      if (option === 'select') {
        fetchObjectNames(type);
      }
    },
    [fetchObjectNames]
  );

  // Handle selection change in dual-list picker
  const handleSelectionChange = useCallback((type: ObjectType, selectedIds: string[]) => {
    setTypeStates((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        selectedIds,
      },
    }));
  }, []);

  // Toggle expansion
  const handleToggleExpand = useCallback(
    (type: ObjectType) => {
      const currentState = typeStates[type];
      if (currentState.option === 'select') {
        setTypeStates((prev) => ({
          ...prev,
          [type]: {
            ...prev[type],
            isExpanded: !prev[type].isExpanded,
          },
        }));

        // Fetch names if expanding
        if (!currentState.isExpanded) {
          fetchObjectNames(type);
        }
      }
    },
    [typeStates, fetchObjectNames]
  );

  // Handle confirm
  const handleConfirm = useCallback(() => {
    const filter: SchemaExportFilter = {
      tables: typeStates.table.option === 'select' ? typeStates.table.selectedIds : typeStates.table.option,
      views: typeStates.view.option === 'select' ? typeStates.view.selectedIds : typeStates.view.option,
      procedures: typeStates.procedure.option === 'select' ? typeStates.procedure.selectedIds : typeStates.procedure.option,
      functions: typeStates.function.option === 'select' ? typeStates.function.selectedIds : typeStates.function.option,
    };
    onConfirm(filter);
  }, [typeStates, onConfirm]);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Initialize with sensible defaults based on counts
      const newStates: Record<ObjectType, ObjectTypeState> = {
        table: { option: counts.tableCount > LARGE_THRESHOLD ? 'all' : 'all', selectedIds: [], isExpanded: false },
        view: { option: counts.viewCount > LARGE_THRESHOLD ? 'all' : 'all', selectedIds: [], isExpanded: false },
        procedure: { option: counts.procedureCount > LARGE_THRESHOLD ? 'none' : 'all', selectedIds: [], isExpanded: false },
        function: { option: counts.functionCount > LARGE_THRESHOLD ? 'none' : 'all', selectedIds: [], isExpanded: false },
      };
      setTypeStates(newStates);
    }
  }, [isOpen, counts, sessionId]);

  // Calculate total object count that will be loaded
  const totalToLoad = activeTypes.reduce((sum, config) => {
    const state = typeStates[config.type];
    if (state.option === 'none') return sum;
    if (state.option === 'all') return sum + config.count;
    return sum + state.selectedIds.length;
  }, 0);

  const totalObjects = activeTypes.reduce((sum, config) => sum + config.count, 0);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-4xl w-[min(64rem,calc(100vw-2rem))] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Large Database Detected
          </DialogTitle>
          <DialogDescription>
            The database <strong>{databaseName}</strong> contains{' '}
            <strong>{totalObjects.toLocaleString()}</strong> objects.
            {largeTypes.length > 0 && (
              <>
                {' '}
                The following types exceed {LARGE_THRESHOLD} items:{' '}
                {largeTypes.map((t) => t.pluralLabel).join(', ')}.
              </>
            )}
            {' '}Choose what to include in the AI context files:
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto py-4 space-y-4">
          {/* MCP note */}
          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
            <Plug className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <strong>Note:</strong> Full schema information is always available to AI agents on-demand
              through the MCP integration. These files provide initial context for faster responses.
            </p>
          </div>

          {activeTypes.map((config) => {
            const state = typeStates[config.type];
            const Icon = config.icon;
            const isLarge = config.count > LARGE_THRESHOLD;

            return (
              <div key={config.type} className="border border-border rounded-lg overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 p-3 bg-muted/30">
                  <Icon className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium flex-1">{config.label}</span>
                  <Badge variant={isLarge ? 'destructive' : 'secondary'}>
                    {config.count.toLocaleString()}
                  </Badge>
                </div>

                {/* Options */}
                <div className="p-3">
                  <RadioGroup
                    value={state.option}
                    onValueChange={(value: FilterOption) => handleOptionChange(config.type, value)}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="none" id={`${sessionId}-${config.type}-none`} />
                      <Label htmlFor={`${sessionId}-${config.type}-none`} className="cursor-pointer text-sm">
                        Don't load
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="all" id={`${sessionId}-${config.type}-all`} />
                      <Label htmlFor={`${sessionId}-${config.type}-all`} className="cursor-pointer text-sm">
                        Load all
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="select" id={`${sessionId}-${config.type}-select`} />
                      <Label htmlFor={`${sessionId}-${config.type}-select`} className="cursor-pointer text-sm">
                        Select specific
                        {state.option === 'select' && state.selectedIds.length > 0 && (
                          <span className="ml-1 text-muted-foreground">
                            ({state.selectedIds.length})
                          </span>
                        )}
                      </Label>
                    </div>
                  </RadioGroup>

                  {/* Expandable dual-list picker */}
                  {state.option === 'select' && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => handleToggleExpand(config.type)}
                        className="flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        {state.isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        {state.isExpanded ? 'Hide' : 'Show'} selection picker
                      </button>

                      {state.isExpanded && (
                        <div className="mt-2">
                          <DualListPicker
                            items={objectNames[config.type]}
                            selectedIds={state.selectedIds}
                            onSelectionChange={(ids) => handleSelectionChange(config.type, ids)}
                            searchPlaceholder={`Search ${config.pluralLabel}...`}
                            height={280}
                            isLoading={loadingNames[config.type]}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex items-center justify-between border-t border-border pt-4">
          <div className="text-sm text-muted-foreground">
            Will export <strong>{totalToLoad.toLocaleString()}</strong> of{' '}
            <strong>{totalObjects.toLocaleString()}</strong> objects
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Continue</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
